import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { AstraButton, AstraParagraph, AstraSelect, AstraTextField, astraTwMerge } from '~/astra'
import { updateRecurringAssignment } from '~/store/di-entity.api'
import { Location, PractitionerStatusDefinition, RecurringAssignmentType } from '~/storm'

import { RecurringAssignmentPreview } from '../RecurringAssignmentPreview'
import { WeekdayDescriptor, weekdayDescriptors } from '../weekdays'

type Props = {
    assignment: RecurringAssignmentType
    onClose?: () => void
    outerPadding?: boolean
}

export const EditRecurringAssignment = ({ assignment, onClose, outerPadding = false }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [previewAssignment, setPreviewAssignment] = useState<RecurringAssignmentType>(assignment)

    const handleUpdateProperty = <Property extends keyof RecurringAssignmentType>(property: Property, value?: RecurringAssignmentType[Property]) => {
        setPreviewAssignment(prev => ({ ...prev, [property]: value }))
    }

    const handleStatusUpdate = (status: PractitionerStatusDefinition | null, weekday: WeekdayDescriptor) => {
        setPreviewAssignment(prev => ({
            ...prev,
            [weekday.assignmentStatusCodeProperty]: status?.code ?? null,
            [weekday.assignmentStatusProperty]: status ?? null,
        }))
    }

    const handleRoomUpdate = (room: Location | null, weekday: WeekdayDescriptor) => {
        setPreviewAssignment(prev => ({
            ...prev,
            [weekday.assignmentRoomCodeProperty]: room?.room_code ?? null,
            [weekday.assignmentRoomProperty]: room ?? undefined,
        }))
    }

    const saveMutation = useMutation({
        mutationFn: () => {
            type PatchBody = Parameters<typeof updateRecurringAssignment>[1]
            const body: PatchBody = {
                entity_status: assignment.entity_status,
                repeat_every: previewAssignment.repeat_every,
                start_on: previewAssignment.start_on,
                title: previewAssignment.title,
                monday_status_code: previewAssignment.monday_status_code,
                monday_room_code: previewAssignment.monday_room_code,
                tuesday_status_code: previewAssignment.tuesday_status_code,
                tuesday_room_code: previewAssignment.tuesday_room_code,
                wednesday_status_code: previewAssignment.wednesday_status_code,
                wednesday_room_code: previewAssignment.wednesday_room_code,
                thursday_status_code: previewAssignment.thursday_status_code,
                thursday_room_code: previewAssignment.thursday_room_code,
                friday_status_code: previewAssignment.friday_status_code,
                friday_room_code: previewAssignment.friday_room_code,
            }

            weekdayDescriptors.forEach(weekDay => {
                body[weekDay.assignmentStatusCodeProperty] = previewAssignment[weekDay.assignmentStatusCodeProperty]
                body[weekDay.assignmentRoomCodeProperty] = previewAssignment[weekDay.assignmentRoomCodeProperty]
            })
            return updateRecurringAssignment(assignment.uuid, body)
        },
        onSuccess: () => {
            onClose?.()
        },
    })

    return (
        <div className="flex flex-col gap-3" ref={containerRef}>
            <div className={astraTwMerge('flex flex-col gap-2', outerPadding ? 'px-2 pt-3' : '')}>
                <AstraTextField
                    fullWidth
                    value={previewAssignment.title}
                    type="text"
                    onChange={e => handleUpdateProperty('title', e.target.value)}
                    placeholder="Tittel"
                />
                <div className="flex justify-between gap-3">
                    <AstraSelect
                        value={previewAssignment.repeat_every}
                        options={[
                            { value: 1, label: 'Gjenta hver uke' },
                            { value: 2, label: 'Gjenta hver 2. uke' },
                            { value: 3, label: 'Gjenta hver 3. uke' },
                            { value: 4, label: 'Gjenta hver 4. uke' },
                        ]}
                        onChange={value => handleUpdateProperty('repeat_every', value)}
                    />
                    <div className="w-28">
                        <AstraSelect
                            value={previewAssignment.start_on % 2 === 0 ? 2 : 1}
                            options={[
                                { value: 1, label: 'Oddetall' },
                                { value: 2, label: 'Partall' },
                            ]}
                            onChange={value => handleUpdateProperty('start_on', value)}
                        />
                    </div>
                </div>
            </div>

            <RecurringAssignmentPreview
                assignment={previewAssignment}
                numberOfWeeks={1}
                onWeekdayClick={({ weekday, status, room }) => {
                    if (status !== undefined) handleStatusUpdate(status, weekday)
                    if (room !== undefined) handleRoomUpdate(room, weekday)
                }}
            />
            {saveMutation.error && <AstraParagraph className="px-4 py-2 text-critical">Det oppstod en feil. Vennligst prøv igjen.</AstraParagraph>}
            <div className={astraTwMerge('flex justify-end gap-2 self-end', outerPadding ? 'px-2 pb-3' : '')}>
                <AstraButton size="small" onClick={onClose} isLoading={saveMutation.isPending} variant="critical-secondary" label="Forkast endringer" />
                <AstraButton size="small" onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending} variant="primary" label="Lagre endringer" />
            </div>
        </div>
    )
}

export default EditRecurringAssignment
